import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { authAPI } from '../../api/services';

const Settings: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');

    const newErrors: Record<string, string> = {};
    if (!currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!newPassword) newErrors.newPassword = 'New password is required';
    else if (newPassword.length < 8) newErrors.newPassword = 'Must be at least 8 characters';
    else if (currentPassword === newPassword) newErrors.newPassword = 'Must differ from current password';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your new password';
    else if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});

    try {
      setLoading(true);
      await authAPI.changePassword({ currentPassword, newPassword });
      setSuccess('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password');
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-400 mt-0.5">Manage your profile and security</p>
      </div>

      {success && <div className="mx-6 mt-3 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">{success}</div>}
      {error && <div className="mx-6 mt-3 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}

      <div className="px-6 py-6">
        {/* Profile card — full width */}
        <div className="flex items-center gap-5 pb-5 border-b border-gray-100 mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-500 text-white flex items-center justify-center text-2xl font-semibold shrink-0">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 capitalize">{user?.name}</h3>
            <p className="text-sm text-gray-400">{user?.email}</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-xs text-gray-400">Level</p>
              <p className="text-sm font-medium text-gray-700">{user?.level || '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Role</p>
              <p className="text-sm font-medium text-gray-700 capitalize">{(user as any)?.organizationRole?.name || user?.role || '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Priority</p>
              <p className="text-sm font-medium text-gray-700">{(user as any)?.organizationRole?.priority || '—'}</p>
            </div>
          </div>
        </div>

        {/* Security section — full width grid */}
        <div className="grid grid-cols-2 gap-8">
          {/* Change Password */}
          <div>
            <span className="text-xs text-gray-500 font-medium mb-4 block">Change Password</span>

            <form onSubmit={handleChangePassword} className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Current Password</label>
                <input type={showPasswords ? 'text' : 'password'} value={currentPassword}
                  onChange={e => { setCurrentPassword(e.target.value); setErrors(prev => ({ ...prev, currentPassword: '' })); }}
                  placeholder="Enter current password"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                {errors.currentPassword && <p className="text-[10px] text-red-500 mt-0.5">{errors.currentPassword}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">New Password</label>
                  <input type={showPasswords ? 'text' : 'password'} value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setErrors(prev => ({ ...prev, newPassword: '' })); }}
                    placeholder="Min. 8 characters"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  {errors.newPassword && <p className="text-[10px] text-red-500 mt-0.5">{errors.newPassword}</p>}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Confirm Password</label>
                  <input type={showPasswords ? 'text' : 'password'} value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
                    placeholder="Repeat new password"
                    className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
                      confirmPassword && newPassword !== confirmPassword ? 'border-red-400' : 'border-gray-200'
                    }`} />
                  {errors.confirmPassword && <p className="text-[10px] text-red-500 mt-0.5">{errors.confirmPassword}</p>}
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={showPasswords} onChange={e => setShowPasswords(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600" />
                  <span className="text-xs text-gray-500">Show passwords</span>
                </label>
                <button type="submit" disabled={loading}
                  className="text-xs bg-gray-900 text-white px-5 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>

          {/* Info panel */}
          <div>
            <span className="text-xs text-gray-500 font-medium mb-4 block">Security Info</span>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 space-y-4">
              <div>
                <span className="text-xs text-gray-500 font-medium mb-1.5 block">Password Requirements</span>
                <ul className="space-y-1">
                  {['Minimum 8 characters', 'Must differ from current password', 'Mix of letters, numbers, and symbols recommended'].map((req, i) => (
                    <li key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <div className="w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <span className="text-xs text-gray-500 font-medium mb-1.5 block">Account Details</span>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Department</span>
                    <span className="text-gray-700">{(user as any)?.primaryDepartmentId?.name || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Organisation Role</span>
                    <span className="text-gray-700 capitalize">{(user as any)?.organizationRole?.name || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Account Type</span>
                    <span className="text-gray-700 capitalize">{user?.role || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
